import type { FC } from "react"
import React, { useEffect, useMemo } from "react"
import { Flex, Icon, Text } from "@chakra-ui/react"
import { BigNumber } from "ethers"
import { useQueries } from "@tanstack/react-query"
import invariant from "tiny-invariant"

import type {
  Delegation,
  Governor,
  Maybe,
  Organization,
  Proposal,
  ProposalDelegateVotesCountQuery,
  VoteType,
} from "query/graphql"
import {
  useProposalVoteModalQuery,
  useProposalDelegateVotesCountQuery,
  ProposalDelegateVotesCountDocument,
} from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import { addressToAccountId } from "web3/helpers/transformers"
import ProposalDescriptionExceptionWrapper from "proposal/components/ProposalDescriptionExceptionWrapper"
import { useNormalizeProposalId } from "proposal/hooks/useNormalizeProposalId"
import { useCastVote } from "voting/hooks/useCastVote"
import { TransactionState } from "web3/types/transaction"
import Loader from "ui/components/icons/Loader"
import VotingModalContainer from "common/components/voting/VotingModalContainer"
import VotingModalVoterData from "common/components/voting/VotingModalVoterData"
import VotingModalVotingPower from "common/components/voting/VotingModalVotingPower"
import VotingModalProposalData from "common/components/voting/VotingModalProposalData"
import VotingModalSupportForm from "common/components/voting/VotingModalSupportForm"
import { fetcher } from "common/helpers/fetcher"
import { useModals } from "common/hooks/useModals"
import type { VotingModalSupportFormValues } from "common/types/voting"
import { useChainReference } from "web3/hooks/useChainReference"
import { useCastVoteBySig } from "voting/hooks/useCastVoteBySig"
import { useMe } from "user/providers/MeProvider"
import { useSignerStore } from "web3/providers/SignerProvider"
import { getAccountIdParams } from "web3/helpers/accountId"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { isSolanaAddress } from "web3/helpers/address"
import { useAddress } from "web3/hooks/useAddress"
import { useMultichainAccount } from "web3/hooks/useMultichainAccount"

type Props = {
  proposalId: string
  onchainId?: Maybe<string>
  governance: Governor
  governanceName: string
  isMultiChain?: boolean
  isActive: boolean
  votableChainIds: string[]
}

const VoteModal: FC<Props> = ({
  proposalId: proposalIdProp,
  onchainId,
  governance,
  governanceName,
  isMultiChain,
  votableChainIds,
  // isActive,
}) => {
  const { organization } = useOrganization()
  const {
    voteModal: { isOpen, onClose },
  } = useModals()
  const { signer } = useSignerStore()

  const voterAddress = useAddress()
  const { multichainAccount } = useMultichainAccount()
  const { type: accountType } = multichainAccount

  const chainReference = useChainReference()

  const { status, castVote } = useCastVote({ chainReference })

  const { proposalId } = useNormalizeProposalId({
    governanceId: governance.id,
    proposalId: proposalIdProp,
  })

  const me = useMe()

  const { chainId } = getAccountIdParams(governance?.id)
  const { reference: governanceReference } = getChainIdParams(chainId)
  const accountId =
    voterAddress && chainReference
      ? addressToAccountId(voterAddress, chainReference)
      : undefined

  const solanaChainId = organization?.chainIds.find((chainId) =>
    chainId.includes("solana"),
  )

  const {
    data: solanaDelegateVotesCount,
    isLoading: isSolanaDelegateVotesCountLoading,
  } = useProposalDelegateVotesCountQuery(
    {
      input: {
        id: proposalId,
        governorId: organization?.governorIds.find((gov) =>
          gov.includes("solana"),
        ),
      },
      accountId: `${solanaChainId}:${voterAddress}`,
    },
    {
      enabled:
        isMultiChain &&
        voterAddress !== undefined &&
        isSolanaAddress(voterAddress),
    },
  )

  const results = useQueries({
    queries: votableChainIds
      .filter((chainId) => !chainId.includes("solana"))
      .map((chainId) => ({
        queryKey: ["ProposalDelegateVotesCount", chainId, voterAddress],
        queryFn: () =>
          fetcher.gql<ProposalDelegateVotesCountQuery>({
            query: ProposalDelegateVotesCountDocument,
            variables: {
              input: {
                id: proposalIdProp,
              },
              accountId: `${chainId}:${voterAddress}`,
            },
          }),
        enabled:
          isMultiChain &&
          voterAddress !== undefined &&
          !isSolanaAddress(voterAddress),
      })),
  })

  const isProposalVotingPowerLoading = results.some((r) => r.isLoading)

  const {
    isLoading,
    isError,
    data: section,
  } = useProposalVoteModalQuery(
    {
      input: {
        id: proposalId,
      },
      accountId:
        voterAddress && isSolanaAddress(voterAddress)
          ? `${solanaChainId}:${voterAddress}`
          : accountId,
      address: me?.type === "SAFE" ? me?.address : (voterAddress as string),
    },
    {
      enabled:
        Boolean(accountId || (voterAddress && isSolanaAddress(voterAddress))) &&
        Boolean(chainReference === governanceReference || isMultiChain) &&
        Boolean(me?.address || voterAddress),
    },
  )

  const votableChains = useMemo(() => {
    if (!isMultiChain) return []

    if (
      voterAddress &&
      isSolanaAddress(voterAddress) &&
      solanaDelegateVotesCount
    ) {
      return [
        {
          id: `delegates-vote:${solanaDelegateVotesCount.proposal.delegateVotesCount}`,
          chainId: solanaChainId,
          votes: solanaDelegateVotesCount.proposal.delegateVotesCount,
        },
      ]
    }

    return results.map((result, index) => ({
      id: `delegatesvotes:${result.data?.proposal.delegateVotesCount}`,
      chainId: votableChainIds.filter((chain) => !chain.includes("solana"))[
        index
      ],
      votes: result.data?.proposal.delegateVotesCount,
    }))
  }, [
    isMultiChain,
    voterAddress,
    solanaDelegateVotesCount,
    results,
    solanaChainId,
    votableChainIds,
  ])

  useEffect(() => {
    if (isMultiChain) {
      return
    }

    if (status.state !== TransactionState.None) {
      onClose()
    }
  }, [status, onClose, isMultiChain])

  const { castVoteBySig, isLoading: isSignMessageLoading } = useCastVoteBySig({
    chainReference,
  })

  const isVoteLoading =
    status.state === TransactionState.Mining || isSignMessageLoading

  if (!isOpen) return null

  if (isLoading) {
    return (
      <Flex
        align="center"
        direction="column"
        justify="center"
        pb={16}
        pt={16}
        px={12}
        w="full"
      >
        <Icon
          animation="hourglass 1.5s infinite"
          as={Loader}
          color="purple.500"
          h={10}
          w={10}
        />
        <Text
          animation="up-and-down 1.5s infinite"
          color="purple.500"
          fontSize="md"
          fontWeight="medium"
        >
          Loading proposal information
        </Text>
      </Flex>
    )
  }

  if (isError) return <Text>Error</Text>

  if (!section || !voterAddress) return null

  const { proposal } = section
  const { delegateVotesCount } = proposal

  invariant(proposal.governor, "governor is required")

  const { contracts, token } = proposal.governor
  const { type: governorType } = contracts.governor
  const { decimals } = token

  const votingPower = () => {
    if (!isMultiChain) {
      return delegateVotesCount || BigNumber.from(0)
    }

    if (!votableChains?.length) {
      return ""
    }

    let bigMultiChainVotingPower: BigNumber = BigNumber.from(0)

    if (!isSolanaAddress(voterAddress)) {
      bigMultiChainVotingPower = (votableChains as Delegation[]).reduce(
        (accum, curr) => BigNumber.from(curr.votes).add(accum),
        BigNumber.from(0),
      )
    } else {
      bigMultiChainVotingPower = BigNumber.from(
        votableChains.find(
          (chain) => chain.chainId && chain.chainId.includes("solana"),
        )?.votes,
      )
    }

    return bigMultiChainVotingPower
  }

  const handleSubmitVote = (values: VotingModalSupportFormValues): void => {
    if (!onchainId) {
      return
    }

    const { support, reason, hasFreeVoting } = values

    if (hasFreeVoting) {
      castVoteBySig({
        onchainId,
        proposalId: proposal.id,
        support: support as VoteType,
        reason,
        signer,
        governance: proposal.governor as Governor,
      })

      return
    }

    castVote({
      onchainId,
      proposalId: proposal.id,
      type: support as VoteType,
      reason,
      signer,
      governance: proposal.governor as Governor,
    })
  }

  return (
    <VotingModalContainer isOpen={isOpen} onClose={onClose}>
      <VotingModalVoterData account={section.accountV2} />
      <VotingModalVotingPower
        isGovernorVote
        decimals={!isSolanaAddress(voterAddress) ? decimals : 6}
        votingPower={votingPower()}
      />
      <ProposalDescriptionExceptionWrapper
        governance={governance}
        proposal={proposal as Proposal}
      >
        {(title) => (
          <VotingModalProposalData
            governanceName={governanceName}
            organization={organization as Organization}
            proposal={proposal as Proposal}
            title={title}
          />
        )}
      </ProposalDescriptionExceptionWrapper>
      <VotingModalSupportForm
        accountType={accountType}
        governanceId={governance.id}
        governorType={governorType}
        isLoading={isVoteLoading}
        isMultiChain={isMultiChain}
        isProposalVotingPowerLoading={
          (!isSolanaAddress(voterAddress) && isProposalVotingPowerLoading) ||
          (isSolanaAddress(voterAddress) && isSolanaDelegateVotesCountLoading)
        }
        onchainId={proposal.onchainId as string}
        proposal={proposal}
        proposalId={proposal.id as string}
        votableChains={votableChains as Delegation[]}
        voterAddress={voterAddress}
        onSubmit={handleSubmitVote}
      />
    </VotingModalContainer>
  )
}

export default VoteModal
